import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type LabRangeType = 'optimal' | 'standard';

interface LabRangeContextValue {
  rangeType: LabRangeType;
  setRangeType: (t: LabRangeType) => void;
}

const LabRangeContext = createContext<LabRangeContextValue | null>(null);

/** Provider for lab range type preference. Persists to localStorage. */
export function LabRangeProvider({ children }: { children: React.ReactNode }) {
  const [rangeType, setRangeTypeState] = useState<LabRangeType>(() => {
    try { return (localStorage.getItem('arete_lab_range') as LabRangeType) || 'optimal'; }
    catch { return 'optimal'; }
  });

  const setRangeType = useCallback((t: LabRangeType) => {
    setRangeTypeState(t);
    try { localStorage.setItem('arete_lab_range', t); } catch {}
  }, []);

  const value = useMemo(() => ({ rangeType, setRangeType }), [rangeType, setRangeType]);

  return (
    <LabRangeContext.Provider value={value}>
      {children}
    </LabRangeContext.Provider>
  );
}

/** Access the current lab range type preference. */
export function useLabRange(): LabRangeContextValue {
  const ctx = useContext(LabRangeContext);
  if (!ctx) throw new Error('useLabRange must be used within LabRangeProvider');
  return ctx;
}
